import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { buildRequestErrorMessage } from './shared';

export const mazdaSqlLogTableOptions = [
  {
    label: '版本上报日志 mazda_versionlog',
    value: 'mazda_versionlog',
    desc: '车型版本上报数据；记录 VIN、设备信息、语言、IP 与版本内容。',
  },
  {
    label: '在线运行日志 mazda_ollog',
    value: 'mazda_ollog',
    desc: '车型在线运行数据；记录 VIN、文件名、车型、诊断版本、网络信息等。',
  },
];

const normalizePage = (value) => {
  const page = Number(value || 1);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
};

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'mazda-model-data.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const tryReadBlobError = async (blob) => {
  try {
    if (!(blob instanceof Blob)) {
      return '';
    }
    const text = await blob.text();
    if (!text) {
      return '';
    }
    try {
      const json = JSON.parse(text);
      return json?.message || json?.detail || text;
    } catch (_) {
      return text;
    }
  } catch (_) {
    return '';
  }
};

const makeDownloadFileName = (table) => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('');
  return `mazda-model-data-${table}-${stamp}.csv`;
};

const readFileNameFromDisposition = (disposition) => {
  const header = String(disposition || '');
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch (_) {
      return utf8Match[1].trim();
    }
  }

  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1]?.trim() || '';
};

const getDownloadFileName = (response, table) => (
  readFileNameFromDisposition(response?.headers?.['content-disposition'])
  || makeDownloadFileName(table)
);

export function useMazdaSqlLogs(mazdaPost, serverName) {
  const sqlLogTable = ref('mazda_versionlog');
  const sqlLogItems = ref([]);
  const sqlLogColumns = ref([]);
  const sqlLogTotal = ref(0);
  const sqlLogHasMore = ref(false);
  const sqlLogExactTotal = ref(true);
  const sqlLogPage = ref(1);
  const sqlLogPageSize = ref(20);
  const sqlLogKeyword = ref('');
  const sqlLogJumpPage = ref('1');
  const sqlLogLoading = ref(false);
  const sqlLogDownloading = ref(false);
  const sqlLogDownloadVisible = ref(false);
  const sqlLogDownloadPhase = ref('idle');
  const sqlLogDownloadElapsedSeconds = ref(0);
  const sqlLogDownloadError = ref('');
  const sqlLogDownloadSnapshot = ref({ table: '', keyword: '' });
  const sqlLogDetailVisible = ref(false);
  const sqlLogDetailRow = ref({});
  let sqlLogDownloadController = null;
  let sqlLogDownloadTimer = null;
  let sqlLogDownloadCancelled = false;

  const sqlLogTotalPages = computed(() => {
    if (!sqlLogExactTotal.value) {
      return sqlLogHasMore.value ? sqlLogPage.value + 1 : Math.max(1, sqlLogPage.value);
    }
    return Math.max(1, Math.ceil((sqlLogTotal.value || 0) / (sqlLogPageSize.value || 20)));
  });
  const sqlLogTableDesc = computed(() => mazdaSqlLogTableOptions.find((item) => item.value === sqlLogTable.value)?.desc || '');
  const sqlLogDetailText = computed(() => JSON.stringify(sqlLogDetailRow.value || {}, null, 2));
  const sqlLogDownloadElapsedText = computed(() => {
    const seconds = sqlLogDownloadElapsedSeconds.value;
    if (seconds < 60) return `${seconds} 秒`;
    const minutes = Math.floor(seconds / 60);
    const remainSeconds = seconds % 60;
    return `${minutes} 分 ${String(remainSeconds).padStart(2, '0')} 秒`;
  });
  const sqlLogPageStatusText = computed(() => {
    if (sqlLogExactTotal.value) {
      return `当前第 ${sqlLogPage.value} / ${sqlLogTotalPages.value} 页`;
    }
    return sqlLogHasMore.value
      ? `当前第 ${sqlLogPage.value} 页，后续还有数据`
      : `当前第 ${sqlLogPage.value} 页，已到末页`;
  });
  const sqlLogTotalText = computed(() => {
    if (sqlLogExactTotal.value) {
      return `共 ${sqlLogTotal.value} 条`;
    }
    return sqlLogHasMore.value
      ? `快速加载中，已到第 ${sqlLogPage.value} 页`
      : `快速加载中，已到末页`;
  });

  const resetSqlLogPagination = () => {
    sqlLogPage.value = 1;
    sqlLogJumpPage.value = '1';
  };

  const resetSqlLogList = () => {
    sqlLogItems.value = [];
    sqlLogColumns.value = [];
    sqlLogTotal.value = 0;
    sqlLogHasMore.value = false;
    sqlLogExactTotal.value = true;
  };

  const stopSqlLogDownloadTimer = () => {
    if (sqlLogDownloadTimer) {
      window.clearInterval(sqlLogDownloadTimer);
      sqlLogDownloadTimer = null;
    }
  };

  const startSqlLogDownloadTimer = () => {
    stopSqlLogDownloadTimer();
    sqlLogDownloadElapsedSeconds.value = 0;
    sqlLogDownloadTimer = window.setInterval(() => {
      sqlLogDownloadElapsedSeconds.value += 1;
    }, 1000);
  };

  const isDownloadCancellation = (error) => (
    sqlLogDownloadCancelled
    || error?.code === 'ERR_CANCELED'
    || error?.name === 'CanceledError'
  );

  const loadSqlLogs = async () => {
    sqlLogLoading.value = true;
    try {
      const response = await mazdaPost('listSqlLog', {
        table: sqlLogTable.value,
        page: sqlLogPage.value,
        pageSize: sqlLogPageSize.value,
        keyword: sqlLogKeyword.value,
        skipTotal: true,
      }, {
        timeout: 60000,
      });
      const data = response.data || {};
      if (data.success) {
        sqlLogItems.value = Array.isArray(data.items) ? data.items : [];
        sqlLogColumns.value = Array.isArray(data.columns) ? data.columns : [];
        sqlLogTotal.value = Number(data.total || 0);
        sqlLogHasMore.value = Boolean(data.hasMore);
        sqlLogExactTotal.value = data.exactTotal !== false;
        sqlLogPage.value = normalizePage(data.page || sqlLogPage.value);
        sqlLogPageSize.value = Number(data.pageSize || sqlLogPageSize.value);
        sqlLogJumpPage.value = String(sqlLogPage.value);
      } else {
        resetSqlLogList();
        ElMessage.error(data.message || '车型数据收集查询失败');
      }
    } catch (error) {
      resetSqlLogList();
      ElMessage.error(buildRequestErrorMessage('车型数据收集查询失败', error));
    } finally {
      sqlLogLoading.value = false;
    }
  };

  const downloadSqlLogs = async () => {
    if (sqlLogDownloading.value) return;

    const requestTable = sqlLogTable.value;
    const requestKeyword = sqlLogKeyword.value;
    sqlLogDownloadController = new AbortController();
    sqlLogDownloadCancelled = false;
    sqlLogDownloading.value = true;
    sqlLogDownloadVisible.value = true;
    sqlLogDownloadPhase.value = 'preparing';
    sqlLogDownloadError.value = '';
    sqlLogDownloadSnapshot.value = {
      table: requestTable,
      keyword: requestKeyword,
    };
    startSqlLogDownloadTimer();

    try {
      const response = await mazdaPost('downloadSqlLog', {
        table: requestTable,
        keyword: requestKeyword,
      }, {
        timeout: 600000,
        responseType: 'blob',
        signal: sqlLogDownloadController.signal,
      });

      if (sqlLogDownloadCancelled) return;

      const blob = response?.data;
      const contentType = response?.headers?.['content-type'] || '';
      if (String(contentType).includes('application/json')) {
        const text = await tryReadBlobError(blob);
        throw new Error(text || '服务器未返回有效下载文件');
      }
      if (!(blob instanceof Blob)) {
        throw new Error('响应文件无效');
      }

      if (sqlLogDownloadCancelled) return;

      sqlLogDownloadPhase.value = 'downloading';
      triggerBlobDownload(blob, getDownloadFileName(response, requestTable));
      sqlLogDownloadVisible.value = false;
      const rowCount = Number(response?.headers?.['x-export-rows'] || 0);
      if (rowCount > 0) {
        ElMessage.success(`已开始下载 ${rowCount} 条车型数据`);
      } else {
        ElMessage.warning('当前筛选条件下没有数据，已下载空表头');
      }
    } catch (error) {
      if (isDownloadCancellation(error)) {
        return;
      }

      let msg = buildRequestErrorMessage('车型数据下载失败', error);
      const data = error?.response?.data;
      if (data instanceof Blob) {
        const text = await tryReadBlobError(data);
        if (text) {
          msg = `车型数据下载失败: ${text}`;
        }
      }
      sqlLogDownloadPhase.value = 'error';
      sqlLogDownloadError.value = msg;
      sqlLogDownloadVisible.value = true;
      ElMessage.error(msg);
    } finally {
      stopSqlLogDownloadTimer();
      sqlLogDownloading.value = false;
      sqlLogDownloadController = null;
    }
  };

  const cancelSqlLogDownload = () => {
    if (!sqlLogDownloading.value) {
      sqlLogDownloadVisible.value = false;
      return;
    }

    sqlLogDownloadCancelled = true;
    sqlLogDownloadPhase.value = 'cancelled';
    sqlLogDownloadController?.abort();
    sqlLogDownloadVisible.value = false;
    stopSqlLogDownloadTimer();
    ElMessage.info('已取消车型数据下载');
  };

  const closeSqlLogDownload = () => {
    if (!sqlLogDownloading.value) {
      sqlLogDownloadVisible.value = false;
    }
  };

  const handleSqlLogTableChange = async () => {
    sqlLogKeyword.value = '';
    resetSqlLogPagination();
    await loadSqlLogs();
  };

  const handleSqlLogSearch = async () => {
    resetSqlLogPagination();
    await loadSqlLogs();
  };

  const handleSqlLogPageChange = async (page) => {
    const nextPage = normalizePage(page);
    if (!sqlLogExactTotal.value && nextPage > sqlLogPage.value && !sqlLogHasMore.value) {
      return;
    }
    sqlLogPage.value = nextPage;
    sqlLogJumpPage.value = String(sqlLogPage.value);
    await loadSqlLogs();
  };

  const handleSqlLogSizeChange = async (size) => {
    sqlLogPageSize.value = Number(size || 20);
    resetSqlLogPagination();
    await loadSqlLogs();
  };

  const handleSqlLogJump = async () => {
    if (!sqlLogExactTotal.value) {
      sqlLogJumpPage.value = String(sqlLogPage.value);
      return;
    }
    const page = Number(sqlLogJumpPage.value);
    if (!Number.isFinite(page) || page < 1) {
      sqlLogJumpPage.value = String(sqlLogPage.value);
      return;
    }
    sqlLogPage.value = Math.min(sqlLogTotalPages.value, Math.max(1, Math.floor(page)));
    sqlLogJumpPage.value = String(sqlLogPage.value);
    await loadSqlLogs();
  };

  const showSqlLogDetail = (row) => {
    sqlLogDetailRow.value = row || {};
    sqlLogDetailVisible.value = true;
  };

  watch(serverName, loadSqlLogs);
  onMounted(loadSqlLogs);
  onUnmounted(() => {
    sqlLogDownloadCancelled = true;
    sqlLogDownloadController?.abort();
    stopSqlLogDownloadTimer();
  });

  return {
    sqlLogTable,
    sqlLogItems,
    sqlLogColumns,
    sqlLogTotal,
    sqlLogHasMore,
    sqlLogExactTotal,
    sqlLogPage,
    sqlLogPageSize,
    sqlLogKeyword,
    sqlLogJumpPage,
    sqlLogTotalPages,
    sqlLogPageStatusText,
    sqlLogTotalText,
    sqlLogLoading,
    sqlLogDownloading,
    sqlLogDownloadVisible,
    sqlLogDownloadPhase,
    sqlLogDownloadElapsedText,
    sqlLogDownloadError,
    sqlLogDownloadSnapshot,
    sqlLogDetailVisible,
    sqlLogDetailRow,
    sqlLogDetailText,
    sqlLogTableDesc,
    loadSqlLogs,
    downloadSqlLogs,
    cancelSqlLogDownload,
    closeSqlLogDownload,
    handleSqlLogTableChange,
    handleSqlLogSearch,
    handleSqlLogPageChange,
    handleSqlLogSizeChange,
    handleSqlLogJump,
    showSqlLogDetail,
  };
}
